import React, { useMemo, useRef } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import QuestionCircleIcon from '@atlaskit/icon/glyph/question-circle';
import { PopupSelect } from '@atlaskit/select';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { ProjectId } from '@atlassian/jira-shared-types/src/general.tsx';
import { PeriodFilterSkeleton } from '@atlassian/jira-skeletons/src/ui/cycle-time-breakdown-report/index.tsx';
import { useSelectedRange } from '../../controllers/selected-date-range';
import { useTableFilterStore } from '../../controllers/top-filter';
import { useProjectId } from '../../services/get-project';
import { useHistoricalCycleTimeData } from '../../services/historical-cycle-time';
import type { BarChartBarData } from '../../services/historical-cycle-time/types';
import { isDataEmpty } from '../../services/historical-cycle-time/utils';
import messages from './messages';

const PeriodFilter = () => {
	const projectId: ProjectId | null = useProjectId();
	const [selectedRange, actions] = useSelectedRange();
	const [filters] = useTableFilterStore();
	const { data, loading, error } = useHistoricalCycleTimeData(projectId, filters);
	const { formatMessage, formatDate } = useIntl();
	const hasReportedError = useRef(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const barData = useMemo(() => (data ? [...data.barData].reverse() : []), [data]);
	const options = useMemo(
		() => [
			{
				label: formatMessage(messages.weekCommencing),
				options: barData
					.filter(({ cycleTimeStatistics }: BarChartBarData) => cycleTimeStatistics !== 0)
					.map((singleBarData) => ({
						label: formatDate(singleBarData.startDateInclusive, {
							month: 'short',
							day: 'numeric',
						}),
						value: singleBarData.startDateInclusive,
					})),
			},
		],
		[barData, formatDate, formatMessage],
	);

	if (error && !hasReportedError.current) {
		hasReportedError.current = true;
		fireErrorAnalytics({
			error,
			meta: {
				id: 'periodFilterAGGError',
				packageName: 'jiraSoftwareCycleTimeBreakdownReport',
			},
		});
		// Element is not shown if there is an error
		// Without this the loader skeleton will stay on the screen
		return null;
	}

	if (loading || !data || !selectedRange) {
		return <PeriodFilterSkeleton />;
	}

	const isEmptyState = isDataEmpty(barData);
	return (
		<Wrapper>
			<div data-test-id="software-cycle-time-breakdown-report.ui.period-filter.div">
				<PopupSelect
					classNamePrefix="period-filter-popup-select"
					searchThreshold={100}
					options={options}
					value={options[0].options.find(
						(o) => o.value.getTime() === selectedRange.startDateInclusive.getTime(),
					)}
					// @ts-expect-error - TS2339 - Property 'label' does not exist on type '{ label: string; value: Date; } | null'. | TS2339 - Property 'value' does not exist on type '{ label: string; value: Date; } | null'.
					onChange={({ label, value }, { action }) => {
						if (action === 'select-option') {
							fireUIAnalytics(
								createAnalyticsEvent({
									action: 'clicked',
									actionSubject: 'selectedWeek',
									actionSubjectId: 'cycleTimeReport',
								}),
							);
							const singleBarData = barData.find((x) => x.startDateInclusive === value);
							if (!singleBarData) {
								throw new Error(`cannot find dateRange from ${label}`);
							}
							actions.setSelectedRange({
								startDateInclusive: singleBarData.startDateInclusive,
								endDateExclusive: singleBarData.endDateExclusive,
							});
						}
					}}
					target={({ isOpen, ...triggerProps }) => (
						<Button
							{...triggerProps}
							isSelected={isOpen}
							iconAfter={<ChevronDownIcon label="" />}
							isDisabled={isEmptyState}
						>
							{formatMessage(messages.weekOf, {
								date: formatDate(selectedRange.startDateInclusive, {
									month: 'short',
									day: 'numeric',
								}),
							})}
						</Button>
					)}
				/>
			</div>
			<Tooltip
				content={formatMessage(messages.helpTooltip)}
				position="right"
				onShow={() => {
					fireUIAnalytics(
						createAnalyticsEvent({
							action: 'hovered',
							actionSubject: 'weeklyCycleTimeHelp',
						}),
					);
				}}
			>
				<InfoIconWrapper>
					<QuestionCircleIcon size="small" label="" />
				</InfoIconWrapper>
			</Tooltip>
		</Wrapper>
	);
};

export default PeriodFilter;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const InfoIconWrapper = styled.div({
	marginLeft: token('space.100', '8px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	fill: token('color.icon.inverse', colors.N800),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.icon', colors.N80),
	cursor: 'pointer',
	paddingTop: token('space.025', '2px'),
});
